
import { EditStore, UndoRedo } from "@/stores";
import { defineComponent, PropType, ref, computed } from "vue";
import EditDownload, { ImageFrame } from "@/components/EditDownload.vue";
import EditTitle from "@/components/EditTitle.vue";
import EditSelectFrame from "@/components/EditSelectFrame.vue";
import { frameToResourceUrl, makeImage, microWait } from "@/lib/shared";
import { VueDraggableNext } from "vue-draggable-next";
import store from "@/stores";
import { ITemplateSet } from "../services/Models";

export default defineComponent({
  name: "EditTemplateCommand",
  components: {
    EditDownload,
    EditTitle,
    EditSelectFrame,
    draggable: VueDraggableNext,
  },
  props: {
    editStore: { type: Object as PropType<EditStore> },
    undo: { type: Object as PropType<UndoRedo> },
  },
  setup(props, context) {
    const isShowDownload = ref(false);
    const isShowSave = ref(false);
    const isShowSelectFrame = ref(false);
    const images = ref<ImageFrame[]>([]);
    const templates = ref<ITemplateSet[]>([]);
    const touchedFrameIndex = ref(0);
    const showDownload = async () => {
      if (props.editStore === undefined) return;
      // 選択状態が映らない様に解除しておく
      props.editStore.selectObject(undefined);
      await microWait();

      const xs: ImageFrame[] = [];
      for (const fe of props.editStore?.state.frameWithEdit) {
        const src = frameToResourceUrl(fe.frame, fe.edit.isBalloonOn); //await fe.editor?.getImage();
        if (src === undefined) return;
        const templateImg = await makeImage(src).catch(() => undefined);
        if (templateImg === undefined) return;

        // 編集後画像の生成
        const makeEdited = async () => {
          const data = await fe.editor?.getImage();
          if (data === undefined) return new Image();
          const editedImg = await makeImage(data).catch(() => undefined);
          if (editedImg === undefined) return new Image();
          return editedImg;
        };
        xs.push(new ImageFrame(templateImg, makeEdited));
      }

      images.value = xs;
      isShowDownload.value = true;
    };

    // 作業状態保存開始時もテンプレート画像を準備する（showDownloadを参考）
    const showSave = async () => {
      if (props.editStore === undefined) return;
      // 選択状態が映らない様に解除しておく
      props.editStore.selectObject(undefined);
      await microWait();

      // コマ入れ替え編集データの場合はサムネイル生成用に画像を準備する
      const xs: ImageFrame[] = [];
      if (props.editStore.state.editData.templateID == undefined) {
        for (const fe of props.editStore?.state.frameWithEdit) {
          const src = frameToResourceUrl(fe.frame, fe.edit.isBalloonOn); //await fe.editor?.getImage();
          if (src === undefined) return;
          const templateImg = await makeImage(src).catch(() => undefined);
          if (templateImg === undefined) return;

          const makeEdited = async () => {
            const data = await fe.editor?.getImage();
            if (data === undefined) return new Image();
            const editedImg = await makeImage(data).catch(() => undefined);
            if (editedImg === undefined) return new Image();
            return editedImg;
          };
          xs.push(new ImageFrame(templateImg, makeEdited));
        }
      }
      images.value = xs;
      isShowSave.value = true;
    };

    // コマ選択画面の表示
    const resource = store.useResource();
    const showSelectFrame = async () => {
      // 著者IDを編集中のテンプレートから求める
      const authorId = props.editStore?.state.template.frames[0].author.id;
      // 著者IDでテンプレートをフィルタリング
      const authorTemplates = resource.data.templateSets.filter((x) =>
        x.frames.some((frame) => frame.author.id == authorId)
      );
      templates.value = authorTemplates;
      // コマ選択ダイアログを開く
      isShowSelectFrame.value = true;
    };

    // コマ選択後処理
    const selectFrame = (templateID: number, order: number) => {
      if (props.editStore === undefined) return;
      // コマ選択ダイアログを閉じる
      isShowSelectFrame.value = false;
      // 選択されたテンプレートを取得
      const selectTemplate = JSON.parse(
        JSON.stringify(
          resource.data.templateSets.find((f) => f.id == templateID)
        )
      ) as ITemplateSet;
      if (selectTemplate == undefined) return;
      // 選択されたテンプレートのコマを取得
      const selectFrame = selectTemplate.frames.find((f) => f.order == order);
      if (selectFrame == undefined) return;

      // 選択したコマで編集データを変更する
      props.editStore.changeFrame(selectFrame);
    };

    // ドラッグ中のコマのインデックスを保持
    const touchedFrame = async (index: number) => {
      touchedFrameIndex.value = index;
    };

    // フレームリストの算出（draggableの参照／更新に必要）
    const frameList = computed({
      get: () => props.editStore?.state.frameWithEdit,
      set: (newValue) => {
        if (newValue == undefined) return;
        props.editStore?.changeFrameWithEdit(newValue, touchedFrameIndex.value);
      },
    });

    return {
      isShowDownload,
      isShowSave,
      isShowSelectFrame,
      images,
      templates,
      showDownload,
      showSave,
      showSelectFrame,
      selectFrame,
      touchedFrame,
      frameToResourceUrl,
      context,
      frameList,
    };
  },
});
